import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import {
  json,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { MantineProvider } from "~/providers/MantineProvider";
import { AuthProvider } from "~/context/AuthContext";
import { getUser } from "~/services/auth/auth.server";
import { GlobalLoader, usePageLoader } from "~/components/GlobalLoader";
import { useUIStore } from "~/store/ui";
import { PermissionsService } from "~/services/users/permissions.server";


// Styles
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/code-highlight/styles.css";
import "@mantine/carousel/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/nprogress/styles.css";
import "@mantine/tiptap/styles.css";
import "mantine-react-table/styles.css";
import "~/assets/styles/globals.css";

// Constants
const INITIAL_LOADING_SCRIPT = `
  (function() {
    const loadingHTML = \`
      <div id="initial-loading" style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.98) 100%);
        backdrop-filter: blur(8px);
        z-index: 9999;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          text-align: center;
        ">
          <div style="
            width: 48px;
            height: 48px;
            border: 4px solid #e9ecef;
            border-top: 4px solid #228be6;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          "></div>
          <div style="
            color: #495057;
            font-size: 14px;
            font-weight: 500;
            letter-spacing: 0.025em;
          ">Yükleniyor...</div>
        </div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    \`;
    
    document.documentElement.insertAdjacentHTML('beforeend', loadingHTML);
    document.documentElement.style.overflow = 'hidden';
  })();
`;

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  },
];

export const meta: MetaFunction = () => [
  { title: "Hub Desk" },
  { name: "description", content: "Hub Desk Application" },
  { name: "viewport", content: "width=device-width, initial-scale=1" },
  { name: "color-scheme", content: "light dark" },
  { name: "theme-color", content: "#228be6" },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getUser(request);

  // Kullanıcı giriş yapmışsa permissions'ları da al
  let permissions: string[] = [];
  if (user) {
    try {
      permissions = await PermissionsService.getUserPermissions(request);
    } catch (error) {
      // Hata durumunda boş permissions ile devam et
    }
  }

  return json({
    user,
    permissions,
    ENV: {
      NODE_ENV: process.env.NODE_ENV,
    },
  });
}

interface LoaderData {
  user: any;
  permissions: string[];
  ENV: {
    NODE_ENV: string;
  };
}

function AppContent() {
  const { ENV } = useLoaderData<LoaderData>();
  const isDevelopment = ENV.NODE_ENV === "development";
  const { isPageLoading, isInitialLoad } = useUIStore();

  // Production'da page loader'ı kullan
  if (!isDevelopment) {
    usePageLoader();
  }

  return (
    <>
      <Outlet />
      {!isDevelopment && (
        <GlobalLoader isVisible={isPageLoading && !isInitialLoad} />
      )}
      <ScrollRestoration />
      <Scripts />
    </>
  );
}

function Document({
  children,
  isDevelopment,
}: {
  children: React.ReactNode;
  isDevelopment: boolean;
}) {
  return (
    <html lang="tr">
      <head>
        <meta charSet="utf-8" />
        <Meta />
        <Links />
        {!isDevelopment && (
          <script
            dangerouslySetInnerHTML={{ __html: INITIAL_LOADING_SCRIPT }}
          />
        )}
      </head>
      <body>{children}</body>
    </html>
  );
}

function App() {
  const { user, ENV } = useLoaderData<LoaderData>();
  const isDevelopment = ENV.NODE_ENV === "development";

  return (
    <Document isDevelopment={isDevelopment}>
      <MantineProvider>
        <AuthProvider initialUser={user}>
          <AppContent />
        </AuthProvider>
      </MantineProvider>
    </Document>
  );
}

export default App;

// Error Boundary
export function ErrorBoundary() {
  const error = useRouteError();
  console.error(error);

  return (
    <Document isDevelopment={false}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif",
          padding: "2rem",
        }}
      >
        <div
          style={{
            textAlign: "center",
            maxWidth: "400px",
          }}
        >
          <h1
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              color: "#343a40",
              marginBottom: "1rem",
            }}
          >
            Bir hata oluştu
          </h1>
          <p
            style={{
              color: "#6c757d",
              marginBottom: "1.5rem",
            }}
          >
            Sayfa yüklenirken beklenmeyen bir hata oluştu.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#228be6",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "0.5rem",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Sayfayı Yenile
          </button>
        </div>
      </div>
    </Document>
  );
}